import React from 'react';
import { invalidate } from '@react-three/fiber';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { collectUsedNcsColorsFromPlan } from '../../../../features/ncsColor';
import { editorStore } from '../../../../store/editorStore';
import {
  getFillColor,
  getFillOpacity,
  normalizeFillPattern,
  numberToHexColor,
  setWallDifferentSideColors,
  setWallSideColor,
  supportsPatternFill,
  updateFillPatternForTargets,
  wallUsesDifferentSideColors,
} from '../../../../utils/fillSelection';
import {
  isWallSideColorsToggleEnabled,
  resolveWallSideAppearance,
} from '../../../../utils/wallFaceSides';
import FillPatternSelector from '../../FillPatternSelector';
import FillColorControl from '../components/FillColorControl';
import OpacityField from '../components/OpacityField';
import PanelSection from '../components/PanelSection';
import type { PropertyPanelController } from '../types';

type FillSectionProps = Pick<PropertyPanelController, 'fillPropsOpen' | 'setFillPropsOpen' | 'handleFillColorChange' | 'flushPendingFillColorUpdate' | 'handleFillOpacityChange' | 'flushPendingFillOpacityUpdate' | 'handleFillOpacityRestore'> & {
  grouped: PropertyPanelController['grouped'];
};

/**
 * Fill color / opacity / pattern for the current selection.
 * Observes the store so pinned “used in plan” NCS swatches stay in sync.
 * Includes wall Side 1 / Side 2 colors when that mode is on.
 */
const FillSection: React.FC<FillSectionProps> = observer(({
  grouped,
  fillPropsOpen,
  setFillPropsOpen,
  handleFillColorChange,
  flushPendingFillColorUpdate,
  handleFillOpacityChange,
  flushPendingFillOpacityUpdate,
  handleFillOpacityRestore,
}) => {
  const { t } = useTranslation();
  const mixed = t('common.mixed');
  const {
    fillSectionObjects,
    firstFillSectionObject,
    sharedFillColor,
    fillOpacityObjects,
    firstFillOpacityObject,
    sharedFillOpacity,
    patternFillObjects,
    sharedPattern,
    hasMixedPatternSelection,
  } = grouped;
  const selectedWall =
    grouped.selectedWalls?.length === 1 ? grouped.selectedWalls[0] : undefined;
  const differentSideColors =
    selectedWall?.lineForm !== 'arc' &&
    Boolean(selectedWall && wallUsesDifferentSideColors(selectedWall));

  const pinnedNcsColors = collectUsedNcsColorsFromPlan();

  if (editorStore.selections.length === 0 || fillSectionObjects.length === 0) {
    return null;
  }

  const hasMixedFillColor =
    fillSectionObjects.length > 1 && sharedFillColor === undefined;
  const currentFillHex = firstFillSectionObject
    ? (sharedFillColor ?? numberToHexColor(getFillColor(firstFillSectionObject)))
    : '#ffffff';
  const sideOneHex = selectedWall
    ? numberToHexColor(
        resolveWallSideAppearance(selectedWall, 'left').fillColor ??
          getFillColor(selectedWall),
      )
    : '#ffffff';
  const sideTwoHex = selectedWall
    ? numberToHexColor(
        resolveWallSideAppearance(selectedWall, 'right').fillColor ??
          getFillColor(selectedWall),
      )
    : '#ffffff';

  return (
    <PanelSection
      title={t('propertyPanel.fill')}
      collapsible
      open={fillPropsOpen}
      onToggle={() => setFillPropsOpen(!fillPropsOpen)}
      maxHeightClassName="max-h-[820px]"
    >
      <div className="space-y-0.5 px-3 pb-3 pt-0">
        {firstFillSectionObject && (
          <div key={`fill-section-${firstFillSectionObject.id}`}>
            {/* Shared color + NCS only when walls are not in Side 1 / Side 2 mode. */}
            {!differentSideColors && (
              <FillColorControl
                value={currentFillHex}
                mixed={hasMixedFillColor}
                mixedLabel={mixed}
                pinnedColors={pinnedNcsColors}
                onChange={(value) => handleFillColorChange(fillSectionObjects, value)}
                onCommit={flushPendingFillColorUpdate}
                onClear={() => {
                  handleFillColorChange(fillSectionObjects, '#ffffff');
                  flushPendingFillColorUpdate();
                }}
              />
            )}
            {selectedWall &&
              selectedWall.lineForm !== 'arc' &&
              (differentSideColors || isWallSideColorsToggleEnabled()) && (
              <>
                <div className="flex items-center justify-between gap-3 py-2">
                  <span className="text-sm leading-snug text-gray-900">
                    {t('propertyPanel.differentWallSideColors')}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={differentSideColors}
                    aria-label={t('propertyPanel.differentWallSideColors')}
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors ${
                      differentSideColors
                        ? 'border-black/20 bg-boniYellowPrimary'
                        : 'border-gray-300 bg-gray-200'
                    }`}
                    onClick={() => {
                      setWallDifferentSideColors(
                        selectedWall.id,
                        !differentSideColors,
                      );
                      invalidate();
                    }}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${
                        differentSideColors
                          ? 'translate-x-[18px]'
                          : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
                {differentSideColors && (
                  <div className="border-t border-gray-200 pt-1">
                    <FillColorControl
                      label={t('propertyPanel.wallSideOne')}
                      value={sideOneHex}
                      pinnedColors={pinnedNcsColors}
                      onChange={(value) => {
                        setWallSideColor(selectedWall.id, 'left', value);
                        invalidate();
                      }}
                      onClear={() => {
                        setWallSideColor(selectedWall.id, 'left', '#ffffff');
                        invalidate();
                      }}
                    />
                    <FillColorControl
                      label={t('propertyPanel.wallSideTwo')}
                      value={sideTwoHex}
                      pinnedColors={pinnedNcsColors}
                      onChange={(value) => {
                        setWallSideColor(selectedWall.id, 'right', value);
                        invalidate();
                      }}
                      onClear={() => {
                        setWallSideColor(selectedWall.id, 'right', '#ffffff');
                        invalidate();
                      }}
                    />
                  </div>
                )}
              </>
            )}
            {firstFillOpacityObject && (
              <OpacityField
                value={sharedFillOpacity ?? getFillOpacity(firstFillOpacityObject)}
                mixed={fillOpacityObjects.length > 1 && sharedFillOpacity === undefined}
                mixedLabel={mixed}
                onChange={(value) => handleFillOpacityChange(fillOpacityObjects, value)}
                onCommit={flushPendingFillOpacityUpdate}
                onRestore={() => handleFillOpacityRestore(fillOpacityObjects)}
              />
            )}
            {editorStore.selections.length === 1 &&
              supportsPatternFill(firstFillSectionObject) && (
                <FillPatternSelector
                  object={firstFillSectionObject}
                  currentPattern={normalizeFillPattern(
                    firstFillSectionObject.fillPattern,
                  )}
                  onUpdate={(_object, _property, value) =>
                    updateFillPatternForTargets(firstFillSectionObject, value)
                  }
                />
              )}
          </div>
        )}
        {editorStore.selections.length > 1 && patternFillObjects.length > 0 && (
          <FillPatternSelector
            object={patternFillObjects[0]}
            currentPattern={sharedPattern}
            isMixed={hasMixedPatternSelection}
            onUpdate={(_object, _property, value) =>
              updateFillPatternForTargets(patternFillObjects, value)
            }
          />
        )}
      </div>
    </PanelSection>
  );
});

export default FillSection;
